/**
 * documentWriteExecutor — `write_markdown`, poser un document entier en UN appel
 * (#993).
 *
 * ## Le défaut que ce verbe ferme
 *
 * Il n'existait aucun chemin « markdown → document mis en forme » hors de la
 * cascade de génération d'actes. Le chemin riche — parseur structurel partagé,
 * conversion, listes natives, tables natives, normalisation — était réservé aux
 * actes du catalogue ; pour un mail, un courrier, une note ou une trame, l'agent
 * ne disposait que d'insertions de texte nu. Un mail de vingt-cinq lignes
 * arrivait en huit appels, sans un titre ni un gras.
 *
 * ## Ce que cet exécuteur fait, et surtout ce qu'il NE fait pas
 *
 * Il **branche une entrée**, il ne construit pas un tuyau. Deux appels, et rien
 * entre les deux :
 *  1. `StructuralParser` — le parseur partagé, celui de la génération en flux et
 *     de la génération orchestrateur. Il n'y en a qu'un, c'est l'invariant : un
 *     second ferait diverger le rendu d'un acte et celui d'un mail dès le
 *     premier correctif de peintre.
 *  2. `ctx.ecrireDocument` — le point d'écriture unique (`canvasWritePoint`),
 *     avec sa file sérialisée, son attente bornée et sa normalisation
 *     structurelle.
 *
 * `mode:"replace"` initialise exclusivement un Canvas que le broker a déjà
 * prouvé vide. Aucun verbe n'écrase un document non vide : il se change en
 * place, même quand l'avocat demande de le réécrire en entier ; une page neuve
 * (`open_canvas_document`) sert à écrire un AUTRE document. Cet exécuteur ne supprime donc jamais d'ancien
 * CONTENU : écrire puis supprimer est précisément le fallback qui pouvait
 * laisser coexister deux actes après une pose partielle.
 *
 * ## Le paragraphe vide d'un DOCX neuf passe AUSSI par le point d'écriture
 *
 * Un DOCX neuf porte un paragraphe physique vide, obligatoire en OOXML. Le point
 * d'écriture ajoute en fin de document, donc ce paragraphe restait visible
 * au-dessus de l'acte. Du 2026-09-21 (#1676) au 2026-09-24, on l'a fait
 * disparaître en confiant toute l'initialisation à l'import Markdown natif
 * (`doc.replace`) — un SECOND rendu, qui ne pose ni les styles `Hector-*` ni la
 * police de la charte : l'acte exporté en Word sortait dans la police par
 * défaut, titres non centrés, corps non justifié. On écrit donc par le point
 * d'écriture, puis on retire ce seul paragraphe, reprouvé vide juste avant.
 * Son retrait est best-effort : une ligne vide restante est cosmétique, alors
 * qu'un échec pousserait l'agent à réécrire et donc à dupliquer l'acte.
 *
 * ## L'écriture est DIRECTE, et le receipt le dit
 *
 * Le point d'écriture pose ses blocs et leurs finitions en `changeMode:'direct'`
 * — quel que soit le régime de révision affiché dans la barre du canvas, donc
 * quel que soit le mode de l'éditeur. C'est l'exception nommée de #990, actée au catalogue partagé
 * (`reviewPolicy:'direct'`) : soumettre un acte de deux cents paragraphes comme
 * autant de révisions à accepter une par une n'a aucun sens pour l'avocat, et
 * l'éditeur ne sait de toute façon pas le faire (une insertion HTML en mode
 * suivi est acceptée et ne produit AUCUNE révision, mesuré le 2026-08-16).
 *
 * Le receipt ne l'écrit pourtant **pas en dur** : les identités de révision sont
 * relues avant et après, et `reviewability` en découle
 * (`direct_untracked` quand rien n'a été tracké). Une valeur figée mentirait le
 * jour où ce chemin produirait des révisions ; la relecture, elle, suivra.
 */

import { CANVAS_RETIRED_ACTIONS, StructuralParser, type CanvasActionInput, type StructuredBlock } from '@hector/shared-types';
import { abortedBeforeMutation, buildVerifiedReceipt, inconclusiveAfterMutation, toCanvasSnapshotSummary, type CanvasActionReceipt, type VerificationCheck } from './actionReceipt';
import { estArretDeLot } from './executionAsynchrone';
import { detailDuRefus, estUnRefus } from './formatActionExecutors';
import { muterSousFences, revisionCourante, type RevisionDeDocument } from './fencesDeMutation';
import { leCorpsDuModeleWordInitialEstToujoursExact, lePremierParagrapheEstToujoursSansInline } from './modeleWordInitial';
import { idsDeRevision, operationDuDocument, readCompleteTrackedChangesAtDocumentRevision, type CanvasSnapshotBlock } from './canvasSnapshot';
import { scanAllBlocks, snapshot, targetSummary, type CanvasActionContext } from './textActionExecutors';
import { CAUSE_EXPORT_EN_COURS, CODE_ENREGISTREMENT_EN_COURS } from './refusPassager';
import { RefusTransitoireDuProvider, type ResultatEcriture } from '../../editor/canvasWritePoint';

type WriteMarkdownInput = Extract<CanvasActionInput, { action: 'write_markdown' }>;

/**
 * Longueur minimale d'un témoin de texte utilisable comme postcondition.
 *
 * En dessous, la chaîne a trop de chances d'exister déjà ailleurs dans le
 * document : la postcondition passerait au vert sur du contenu que l'action n'a
 * pas écrit — un capteur aveugle, exactement ce qu'on refuse.
 */
const LONGUEUR_MINIMALE_DU_TEMOIN = 8;
/** Borné : un témoin trop long traverserait une césure de run et ne matcherait jamais. */
const LONGUEUR_MAXIMALE_DU_TEMOIN = 60;

/**
 * Le texte brut porté par un bloc structuré, dans l'ordre de lecture.
 *
 * Les trois familles de blocs rangent leurs runs à des endroits différents ; une
 * lecture qui n'en connaîtrait qu'une rendrait une chaîne vide sur un markdown
 * qui commence par une liste ou par une table, et la postcondition qui s'appuie
 * dessus deviendrait inévaluable sans le dire.
 */
function texteDuBloc(bloc: StructuredBlock): string {
  if (bloc.kind === 'paragraph') return bloc.runs.map((run) => run.text).join('');
  if (bloc.kind === 'list') return bloc.items.map((item) => item.runs.map((run) => run.text).join('')).join(' ');
  return bloc.rows
    .flatMap((row) => row.cells.flatMap((cell) => cell.paragraphs.map((paragraphe) => paragraphe.runs.map((run) => run.text).join(''))))
    .join(' ');
}

/**
 * Une chaîne du markdown REÇU qu'on doit retrouver dans les blocs parsés, puis
 * dans le document après écriture.
 *
 * ⚠️ Tirée du markdown reçu, jamais des blocs parsés. Un témoin pris dans la
 * sortie du parseur ne peut pas voir une perte survenue DANS le parseur : il
 * confirmait un courrier réduit à ses 64 derniers caractères (reçu vert,
 * `document_contains_written_text` passée sur « ion manuscrite », production,
 * 2026-10-08). La première tranche lisible du markdown est donc le DÉBUT du
 * document, la partie que cette perte emportait.
 *
 * Les cibles de lien et les balises `[Hector-XX]` / `[/PARA]` sont retirées
 * avant la découpe : ce sont des mots du balisage, pas du texte du document.
 * Mesuré sur les 3 016 `write_markdown` de production du 2026-09-25 au
 * 2026-10-09 : le témoin se retrouve dans les blocs parsés pour chacun.
 *
 * ⚠️ Bornée aux lettres, chiffres et espaces, délibérément. Le canal HTML du
 * provider laisse `&`, `<` et `>` **échappés en toutes lettres** dans le
 * document (mesuré le 2026-08-15, #980) : un témoin qui en contiendrait ne
 * matcherait jamais, et la garde échouerait sur une écriture pourtant réussie.
 *
 * Rend `undefined` quand aucune tranche assez longue n'existe — et l'appelant en
 * fait alors une postcondition ÉCHOUÉE, jamais un succès par défaut : une garde
 * qui ne sait pas conclure ne doit pas laisser passer.
 */
export function temoinDuMarkdownRecu(markdown: string): string | undefined {
  const lisible = markdown
    .replace(/\]\([^)]*\)/g, ']')
    .replace(/\[\/?Hector-[\w-]+\]/g, ' ')
    .replace(/\[\/[A-Z:]+[\]>]/g, ' ');
  for (const tranche of lisible.split(/[^\p{L}\p{N} ]+/u)) {
    const propre = tranche.trim().replace(/\s+/g, ' ');
    if (propre.length >= LONGUEUR_MINIMALE_DU_TEMOIN) return propre.slice(0, LONGUEUR_MAXIMALE_DU_TEMOIN);
  }
  return undefined;
}

function texteDesBlocs(blocs: readonly StructuredBlock[]): string {
  return blocs.map(texteDuBloc).join('\n').replace(/\s+/g, ' ');
}

function texteDuDocument(blocs: readonly CanvasSnapshotBlock[]): string {
  return blocs.map((bloc) => bloc.fullText).join('\n').replace(/\s+/g, ' ');
}

interface MarkdownAnalyse {
  ok: true;
  blocs: StructuredBlock[];
}
interface MarkdownRefuse {
  ok: false;
  code: string;
  message: string;
}

/**
 * Traverse le parseur partagé — `push()` PUIS `finalize()`, sans exception.
 *
 * Un markdown d'agent n'a aucun délimiteur `[/PARA]` : sans `finalize()`, le
 * drain n'émettrait rien du tout (#931).
 *
 * ⚠️ **`preambule: 'conserver'` est load-bearing.** Par défaut le parseur jette
 * tout ce qui précède le premier `#` ou `[Hector-XX]` : c'est le préambule d'un
 * FLUX de génération d'acte. Le markdown d'un agent est un document entier, et
 * un courrier commence par l'expéditeur, pas par un titre. Sans cette option,
 * un courrier sans `#` était réduit à ses 64 derniers caractères (la fenêtre de
 * détection du parseur, émise par `finalize()`), et un courrier dont le premier
 * `#` est l'objet perdait son en-tête — sous un reçu vert (85 conversations de
 * production, 2026-09-25 → 2026-10-09).
 *
 * La garde qui suit le parseur fait respecter l'invariant au lieu de le
 * supposer : le début du markdown reçu doit se retrouver dans les blocs, sinon
 * rien n'est écrit.
 */
export function analyserLeMarkdown(markdown: string): MarkdownAnalyse | MarkdownRefuse {
  const blocs: StructuredBlock[] = [];
  try {
    const parseur = new StructuralParser({ onBlock: (bloc) => blocs.push(bloc) }, { preambule: 'conserver' });
    parseur.push(markdown);
    parseur.finalize();
  } catch (erreur) {
    return {
      ok: false,
      code: 'MARKDOWN_PARSE_FAILED',
      message:
        `write_markdown: le parseur structurel partagé a échoué sur les ${markdown.length} caractères de markdown fournis `
        + `(${erreur instanceof Error ? erreur.message : String(erreur)}). RIEN n'a été écrit dans le document. `
        + 'Simplifie la structure du markdown (titres, listes, tables) et rappelle le verbe.',
    };
  }
  if (blocs.length === 0) {
    return {
      ok: false,
      code: 'MARKDOWN_PRODUCED_NO_BLOCK',
      message:
        `write_markdown: les ${markdown.length} caractères de markdown fournis n'ont produit AUCUN bloc structuré, `
        + 'donc RIEN n\'a été écrit dans le document. Le markdown ne contient que du balisage (délimiteurs, balises, '
        + 'bloc de réflexion) et aucun texte de document : renvoie le texte du document lui-même, puis rappelle '
        + 'write_markdown.',
    };
  }
  const temoin = temoinDuMarkdownRecu(markdown);
  if (temoin !== undefined && !texteDesBlocs(blocs).includes(temoin)) {
    return {
      ok: false,
      code: 'MARKDOWN_PARSE_LOST_TEXT',
      message:
        `write_markdown: le parseur structurel partagé a perdu le début du markdown fourni (${markdown.length} caractères reçus, `
        + `${blocs.length} bloc(s) produits, début attendu « ${temoin} » absent des blocs). RIEN n'a été écrit dans le `
        + 'document. Ce n\'est pas une erreur de ton markdown : ne le reformule pas en boucle. Donne le texte à l\'avocat '
        + 'dans la conversation et dis-lui que l\'écriture dans le canvas a échoué.',
    };
  }
  return { ok: true, blocs };
}

type RetraitDuModele = { retire: true } | { retire: false; raison: string };

/**
 * Retire le paragraphe vide d'un DOCX neuf APRÈS l'écriture stylée.
 *
 * Ce paragraphe n'a jamais porté de contenu : il est reprouvé premier, sans
 * texte (API blocks) ET sans inline (forme SDM) juste avant l'appel — l'écriture
 * a duré plusieurs secondes, et un logo posé entre-temps dans cette ligne n'a
 * aucun texte. Seul lui est ciblé. Un échec est rendu, jamais levé — hormis
 * l'arrêt de lot d'époque, qui doit remonter comme partout.
 */
async function retirerLeParagrapheVideDuModele(ctx: CanvasActionContext, nodeId: string): Promise<RetraitDuModele> {
  // Liée à sa famille : détachée, l'opération perd le receveur qu'elle lit, lève, et le `catch` ci-dessous en ferait
  // « blocks.delete threw » — la ligne vide du modèle resterait en tête de l'acte.
  const supprimer = operationDuDocument(ctx.doc, 'blocks', 'delete');
  if (supprimer === undefined) return { retire: false, raison: 'editor.doc.blocks.delete is unavailable' };
  const blocs = await scanAllBlocks(ctx);
  const [premier] = blocs;
  if (premier === undefined || premier.nodeId !== nodeId || premier.fullText !== '' || blocs.length < 2
    || !await lePremierParagrapheEstToujoursSansInline(ctx)) {
    return { retire: false, raison: 'it is no longer an empty first paragraph (text, image or field was added meanwhile), so it was kept' };
  }
  try {
    const attendue = await revisionCourante(ctx, '');
    const resultat = await muterSousFences(
      ctx,
      'write_markdown:paragraphe-vide-du-modele',
      { changeMode: 'direct', expectedRevision: attendue },
      () => supprimer({ target: { kind: 'block', nodeType: 'paragraph', nodeId } }, { changeMode: 'direct', expectedRevision: attendue }),
    );
    if (estUnRefus(resultat)) {
      return { retire: false, raison: `blocks.delete answered success:false ${detailDuRefus(resultat)}` };
    }
    return { retire: true };
  } catch (erreur) {
    if (estArretDeLot(erreur)) throw erreur;
    return { retire: false, raison: `blocks.delete threw: ${erreur instanceof Error ? erreur.message.slice(0, 160) : String(erreur).slice(0, 160)}` };
  }
}

/** Vrai si `comments.list` rend l'enveloppe de découverte d'une liste vide ; un tableau nu ou un échec ne prouve rien. */
function prouveZeroCommentaire(reponse: unknown): boolean {
  if (typeof reponse !== 'object' || reponse === null || Array.isArray(reponse)) return false;
  const { success, total, items } = reponse as { success?: unknown; total?: unknown; items?: unknown };
  return success !== false && total === 0 && Array.isArray(items) && items.length === 0;
}

/**
 * Prouve AU MOMENT D'ÉCRIRE qu'un corps est le seul paragraphe vide d'un DOCX
 * neuf, quand l'enveloppe ne porte pas l'autorisation du broker.
 *
 * Mesuré en production le 2026-10-13 (16 appels, 14 conversations, 5
 * échantillons sur 5) : le document est vierge (1 bloc, 0 texte) et l'agent
 * vient de le lire, mais l'autorisation n'est armée que par certaines formes de
 * lecture ; `write_markdown mode:"replace"` refusait alors un document vide
 * comme « non vide ».
 *
 * La preuve est celle que l'inspection fait pour armer l'autorisation : un seul
 * paragraphe sans texte ni inline (forme SDM), aucune révision ouverte dans
 * aucune partie du document, aucun commentaire même résolu — le tout sous UNE
 * révision, relue avant et après, que l'écriture reprend comme révision attendue.
 * Une lecture absente, illisible ou d'une forme inconnue ne prouve rien.
 */
async function prouverLeDocumentViergeALEcriture(
  ctx: CanvasActionContext,
  blocs: readonly CanvasSnapshotBlock[],
): Promise<{ nodeId: string; revision: RevisionDeDocument } | undefined> {
  const [seul] = blocs;
  if (blocs.length !== 1 || seul === undefined || seul.nodeType.toLowerCase() !== 'paragraph'
    || seul.fullText !== '' || seul.textTruncated !== undefined) {
    return undefined;
  }
  // Liée à sa famille : une opération du fournisseur lit son receveur, et appelée détachée elle lève — le `catch`
  // ci-dessous en ferait « pas de preuve », donc un document vierge refusé comme non vide (revue de la PR #2257).
  const listerLesCommentaires = operationDuDocument(ctx.doc, 'comments', 'list');
  if (listerLesCommentaires === undefined) return undefined;
  try {
    const revision = await revisionCourante(ctx, '');
    // `maxTrackedChanges: 0` : la lecture exhaustive échoue dès qu'une révision existe, et borne elle-même la révision.
    const revisions = await readCompleteTrackedChangesAtDocumentRevision(ctx.doc, revision, { maxTrackedChanges: 0 });
    if (!revisions.ok || revisions.trackedChanges.length > 0) return undefined;
    const sansCommentaire = prouveZeroCommentaire(
      await listerLesCommentaires({ includeResolved: true, includeThreadReplies: true, limit: 1, offset: 0 }),
    );
    if (!sansCommentaire || !await leCorpsDuModeleWordInitialEstToujoursExact(ctx)) return undefined;
    return await revisionCourante(ctx, '') === revision ? { nodeId: seul.nodeId, revision } : undefined;
  } catch {
    return undefined;
  }
}

export async function executeWriteMarkdown(ctx: CanvasActionContext, input: WriteMarkdownInput): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const refus = (code: string, message: string): CanvasActionReceipt =>
    abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code, message });

  const analyse = analyserLeMarkdown(input.markdown);
  if (!analyse.ok) return refus(analyse.code, analyse.message);

  // Le point d'écriture est un objet PAR MONTAGE du canvas, posé dans le store
  // par `RedactionCanvas`. Son absence n'est pas une incapacité du document :
  // c'est un canvas non monté, ou monté à l'instant. On refuse donc AVANT toute
  // mutation, en nommant la sortie praticable.
  const ecrire = ctx.ecrireDocument;
  if (!ecrire) {
    return refus(
      'CANVAS_WRITE_POINT_UNAVAILABLE',
      `write_markdown: le canvas n'a pas de point d'écriture (aucun document monté, ou montage en cours), donc les `
      + `${analyse.blocs.length} blocs n'ont PAS été écrits et le document est intact. Appelle open_canvas, puis `
      + 'canvas_inspect pour confirmer que la session est prête, avant de rappeler write_markdown.',
    );
  }

  // `replace` reste l'initialisation d'un Canvas prouvé vide. Le remplacement
  // d'un corps existant par write-then-delete a été retiré : si la pose échoue
  // partiellement, elle ne doit jamais pouvoir créer un second acte à côté du
  // premier.
  const blocsAvant = input.mode === 'replace' ? await scanAllBlocks(ctx) : [];
  // L'autorisation du broker désigne le paragraphe vide d'un DOCX neuf. Elle ne
  // vaut que si le corps est TOUJOURS exactement ce paragraphe : texte (API
  // blocks) ET forme SDM, qui seule voit une image ou un champ sans texte.
  let paragrapheVideDuModele: string | undefined;
  let revisionDeLaPreuve: RevisionDeDocument | undefined;
  if (input.mode === 'replace' && ctx.initialBlankTemplateParagraphId !== undefined) {
    const [seul] = blocsAvant;
    const intact = blocsAvant.length === 1
      && seul !== undefined
      && seul.nodeId === ctx.initialBlankTemplateParagraphId
      && seul.nodeType.toLowerCase() === 'paragraph'
      && seul.fullText === ''
      && seul.textTruncated === undefined
      && await leCorpsDuModeleWordInitialEstToujoursExact(ctx);
    if (!intact) {
      return refus(
        'INITIAL_BLANK_TEMPLATE_CHANGED',
        `write_markdown mode:"replace" was authorised for the single initial empty Word paragraph ${ctx.initialBlankTemplateParagraphId}, `
        + `but the live body now holds ${blocsAvant.length} block(s) or that paragraph carries content without text (image, field). `
        + 'Nothing was written; run canvas_inspect, then add with write_markdown mode:"append" or edit the current document in place.',
      );
    }
    paragrapheVideDuModele = seul.nodeId;
  } else if (input.mode === 'replace' && blocsAvant.length > 0) {
    const vierge = await prouverLeDocumentViergeALEcriture(ctx, blocsAvant);
    if (vierge === undefined) {
      // Un corps fait de plusieurs paragraphes VIDES n'est pas « vierge » (un seul
      // paragraphe sentinelle) : sans issue nommée, l'agent rejouait le même refus.
      const blocsAvecTexte = blocsAvant.filter((bloc) => bloc.fullText.trim().length > 0).length;
      return refus(
        'WRITE_MARKDOWN_REPLACE_NONEMPTY',
        'write_markdown mode:"replace" only initialises a blank document. This one contains '
        + `${blocsAvant.length} block(s), ${blocsAvecTexte} with text, so nothing was written. `
        + (blocsAvecTexte === 0
          ? 'They are all empty paragraphs: write your document with write_markdown mode:"append" (it lands after them), '
            + 'then remove the leading empty paragraphs with delete_block_range if they are not wanted.'
          // L'ordre est celui de la règle : un document qui a du contenu se change
          // en place ; l'ajout en fin vient après, la page neuve en dernier — lue
          // en premier, elle envoyait vers un document neuf une simple retouche.
          // Les deux issues qui encadrent l'ajout sont celles du verbe retiré
          // `replace_document` (même intention : écraser le document), lues à leur
          // source unique.
          : `${CANVAS_RETIRED_ACTIONS.replace_document.aLaPlace.enPlace} `
            + 'To add a section at its end, use write_markdown mode:"append". '
            + CANVAS_RETIRED_ACTIONS.replace_document.aLaPlace.documentNeuf),
      );
    }
    paragrapheVideDuModele = vierge.nodeId;
    revisionDeLaPreuve = vierge.revision;
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);

  // Le point d'écriture pose ses blocs en direct, par ses propres appels. On
  // passe quand même par `muterSousFences` — c'est lui
  // qui ré-assère l'époque juste avant, et surtout qui marque qu'une mutation a
  // été TENTÉE. Sans ce marquage, un changement de document pendant l'écriture
  // ferait rendre « refusé, rien n'a bougé » sur un document qu'on vient
  // peut-être de remplir.
  const resultat = await muterSousFences(
    ctx,
    'write_markdown:ecriture',
    { changeMode: 'direct', expectedRevision: revisionDeLaPreuve ?? await revisionCourante(ctx, '') },
    () => {
      const metadata = {
        ...(ctx.canvasRequestId === undefined ? {} : { requestId: ctx.canvasRequestId }),
        ...(ctx.onChunkCompleted === undefined ? {} : { onChunkCompleted: ctx.onChunkCompleted }),
      };
      return Object.keys(metadata).length === 0
        ? ecrire(analyse.blocs)
        : ecrire(analyse.blocs, metadata);
    },
  );

  if (resultat.statut === 'refusee' && resultat.raison === 'travail-remplace') {
    // La file a changé d'époque de travail : le maillon a pu être refusé AVANT
    // d'écrire comme APRÈS son attente. L'effet n'est ni prouvé ni infirmé —
    // et retenter à l'aveugle poserait le document deux fois.
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'WRITE_POINT_WORK_REPLACED',
      message:
        'write_markdown: une autre écriture a pris la place de celle-ci dans le canvas (génération d\'acte relancée, '
        + 'ou document remplacé). Le document PEUT contenir tout ou partie du markdown demandé. Appelle canvas_inspect '
        + 'et décide depuis l\'état réel — ne rappelle pas write_markdown à l\'aveugle.',
    });
  }
  if (resultat.statut === 'partielle') {
    // ⚠️ **Le receipt affirmait ici « le document est intact » — et c'était
    // FAUX** (2026-08-26, conversation CD3 `a7c3adc1`). Un dépassement de borne
    // rendait `{statut:'refusee', raison:'condamnee'}`, la même valeur qu'un
    // refus AVANT toute écriture ; cette branche-ci n'existait pas et le cas
    // tombait dans `WRITE_POINT_REFUSED`. L'agent a donc écrit à l'avocat
    // « rien n'a été écrasé ni dupliqué » pendant que l'assignation s'affichait
    // en double devant lui.
    //
    // Deux choses doivent être dites, et l'ordre compte pour un lecteur qui
    // n'aura pas le contexte : ce qui est DANS le document, puis ce qui n'a pas
    // été supprimé. La seconde est la cause visible du dégât — en mode
    // `replace`, la suppression du contenu précédent est court-circuitée par ce
    // retour (elle vit une dizaine de lignes plus bas), donc l'ancien et le
    // neuf cohabitent.
    const poses = resultat.blocsConfirmes;
    const indetermines = resultat.blocsIndetermines;
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'WRITE_POINT_PARTIAL',
      message:
        `write_markdown: the write was CUT SHORT by the write-point time bound. ${poses} block(s) are confirmed in `
        + `the document, ${indetermines} more were still being laid when the bound fired (their fate is unknown — the `
        + `underlying write cannot be cancelled), and ${resultat.blocsNonEcrits} were never written. The document HAS `
        + 'CHANGED — it is NOT intact.'
        + (input.mode === 'replace'
          ? ' This was an empty-document initialisation, so no previous body was removed. Call canvas_inspect and continue '
            + 'from the real state — do NOT replay the same write_markdown blindly, it would duplicate what already landed.'
          : ' Call canvas_inspect and continue from the real state — do NOT replay the same write_markdown blindly, it '
            + 'would duplicate what already landed.'),
    });
  }
  if (resultat.statut === 'refusee' || resultat.statut === 'rien') {
    const raison = resultat.statut === 'rien'
      ? 'le point d\'écriture n\'avait rien de neuf à poser'
      : resultat.raison === 'flux-en-cours'
        ? 'un acte est en cours de rédaction dans ce canvas et l\'écraserait'
        : resultat.raison === 'condamnee'
          ? 'le point d\'écriture est condamné après un échec précédent'
          : 'le canvas a été démonté';
    return refus(
      'WRITE_POINT_REFUSED',
      `write_markdown: l'écriture a été REFUSÉE avant toute modification (${raison}). Le document est intact et les `
      + `${analyse.blocs.length} blocs n'ont pas été écrits. `
      + (resultat.statut === 'refusee' && resultat.raison === 'flux-en-cours'
        ? 'Attends la fin de la rédaction en cours, dis-le à l\'avocat, puis rappelle write_markdown.'
        // Masquer puis réafficher le canvas ne remonte rien : seul un nouveau
        // montage rend un point d'écriture neuf, donc un rechargement de page.
        : 'Ce point d\'écriture ne se rétablit pas seul : demande à l\'avocat de recharger la page, puis lance '
          + 'canvas_inspect sur le document remonté avant de rappeler write_markdown.'),
    );
  }

  const ecritureAboutie = resultat.statut === 'ecrit';
  const retraitDuModele = ecritureAboutie && paragrapheVideDuModele !== undefined
    ? await retirerLeParagrapheVideDuModele(ctx, paragrapheVideDuModele)
    : undefined;

  const blocsApres = await scanAllBlocks(ctx);
  const texteApres = texteDuDocument(blocsApres);
  const temoin = temoinDuMarkdownRecu(input.markdown);

  const verification: VerificationCheck[] = [
    {
      name: 'write_point_confirmed',
      passed: ecritureAboutie,
      message: ecritureAboutie
        ? 'The shared write point confirmed every parsed block was laid into the document.'
        : `The shared write point failed and condemned the canvas write queue: ${resultat.erreur.message.slice(0, 200)}. `
          + 'Part of the markdown may already be in the document; run canvas_inspect before writing again.',
    },
    {
      name: 'document_contains_written_text',
      // ⚠️ Pas de « pas de témoin ⟹ vert » : une postcondition qu'on ne sait pas
      // évaluer est une postcondition ÉCHOUÉE, sinon la garde est un décor.
      passed: temoin !== undefined && texteApres.includes(temoin),
      message: temoin === undefined
        ? 'No comparable text witness could be derived from the received markdown, so the written content could not be re-read.'
        : `The re-read document must contain the beginning of the received markdown (witness: "${temoin}").`,
    },
  ];

  // Une seule relecture d'après-coup, dont sortent à la fois l'instantané du
  // receipt et les identités de révision (#994) : elles étaient lues deux fois.
  const post = await snapshot(ctx);
  ctx.verifierEpoque('write_markdown:receipt');
  return nommerLExportQuiACondamneLaFile(resultat, buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: blocsApres.slice(0, 3).map((bloc) => targetSummary(bloc)),
    provider: resultat.statut === 'echec' ? { success: false } : { statut: resultat.statut },
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    // ⚠️ MESURÉ, pas supposé : le point d'écriture pose ses blocs et leurs
    // finitions en `changeMode:'direct'`, donc aucune identité de révision ne
    // naît de ce geste, même éditeur en `suggesting`. Exiger une apparition
    // ferait échouer TOUTE écriture réussie tant que l'avocat garde le suivi
    // actif. Les identités restent RELUES : la troisième issue de #939 (une
    // révision disparue sans qu'on l'ait demandé) doit continuer de s'exercer.
    mouvementAttendu: 'aucun',
    verification,
    effects: [{
      kind: 'write_markdown',
      // Écriture directe assumée (#993) : ne jamais annoncer `tracked` ici — le
      // rabattement de `buildVerifiedReceipt` ne va que dans le sens de la
      // prudence, il ne rattraperait pas une promesse trop forte sur une action
      // qui produirait par accident une révision.
      reviewability: 'direct_untracked',
      description: (input.mode === 'replace'
        ? `Initialised the previously empty canvas document with ${analyse.blocs.length} formatted block(s) written directly (no tracked revision).`
        : `Appended ${analyse.blocs.length} formatted block(s) to the canvas document, written directly (no tracked revision).`)
        + (retraitDuModele === undefined || retraitDuModele.retire
          ? ''
          : ` The initial template paragraph ${paragrapheVideDuModele} above the written content was not removed (${retraitDuModele.raison}); `
            + 'this is cosmetic and must NOT trigger a second write_markdown.'),
    }],
  }));
}

/**
 * Fait dire au reçu que la file d'écriture a été condamnée par un EXPORT en
 * cours, quand c'est le cas (#1860).
 *
 * Le point d'écriture reprend trois fois un refus passager, puis condamne la
 * file (`editor/canvasWritePoint.ts`). Sans ce geste, le reçu ne porte que
 * `PROVIDER_FAILURE` : l'agent ne sait ni pourquoi, ni que les autres verbes
 * marchent encore. La cause est lue sur le TYPE de l'erreur — celui que
 * l'écrivain ne pose que pour un refus passager sous un mode inscriptible —
 * jamais sur son message.
 *
 * ⚠️ Le geste proposé n'est PAS « rappelle write_markdown » : la file condamnée
 * le refuserait jusqu'au rechargement de la page, et une tranche a pu être
 * posée en partie.
 */
function nommerLExportQuiACondamneLaFile(
  resultat: ResultatEcriture,
  receipt: CanvasActionReceipt,
): CanvasActionReceipt {
  if (resultat.statut !== 'echec' || !(resultat.erreur instanceof RefusTransitoireDuProvider)) return receipt;
  if (receipt.status === 'ok') return receipt;
  return {
    ...receipt,
    errors: [
      ...receipt.errors,
      {
        code: CODE_ENREGISTREMENT_EN_COURS,
        message: `write_markdown was interrupted: the editor kept refusing the write because ${CAUSE_EXPORT_EN_COURS}. `
          + 'Part of the markdown may '
          + 'already be in the document. The write_markdown queue is now closed until the lawyer reloads the page; the '
          + 'other canvas actions still work. Run canvas_inspect, then add what is missing with insert_paragraphs — do '
          + 'not call write_markdown again in this turn.',
        nextAction: 'Run canvas_inspect, then add the missing part with insert_paragraphs.',
      },
    ],
  };
}
